/**
 * @file ShipmentReadinessList.tsx
 * @description Чипы «готовность к передаче в ТК» и подсказки к ним в сайдбаре
 * новой отгрузки (2026-09-22). Данные — `shipmentReadiness`, здесь только вид:
 * ✓ зелёный, ⚠ жёлтый, ✗ красный; подсказки печатаем только у проблемных.
 */

import { AlertTriangle, Check, X } from "lucide-react";

import type { ReadinessCheck } from "../../lib/shipment-readiness";
import { Badge } from "../ui/badge";
import { SectionLabel } from "../ui/SectionLabel";

interface Props {
  checks: ReadinessCheck[];
  carrierName: string;
}

const VARIANT: Record<ReadinessCheck["level"], "success" | "warning" | "destructive"> = {
  ok: "success",
  warn: "warning",
  fail: "destructive",
};

const STATE: Record<ReadinessCheck["level"], string> = {
  ok: "готово",
  warn: "предупреждение",
  fail: "не готово",
};

function Icon({ level }: { level: ReadinessCheck["level"] }) {
  if (level === "ok") return <Check className="size-3.5" aria-hidden />;
  if (level === "warn") return <AlertTriangle className="size-3.5" aria-hidden />;
  return <X className="size-3.5" aria-hidden />;
}

export function ShipmentReadinessList({ checks, carrierName }: Props) {
  const problems = checks.filter((c) => c.hint);
  return (
    <section aria-label="Готовность к передаче" className="flex flex-col gap-2">
      <SectionLabel>Готовность к передаче в {carrierName}</SectionLabel>
      <ul className="flex flex-wrap gap-1.5">
        {checks.map((c) => (
          <li key={c.key}>
            <Badge variant={VARIANT[c.level]} aria-label={`${c.label}: ${STATE[c.level]}`}>
              <Icon level={c.level} />
              {c.label}
            </Badge>
          </li>
        ))}
      </ul>
      {problems.length > 0 ? (
        <ul className="flex flex-col gap-1 text-xs text-on-surface-variant">
          {problems.map((c) => (
            <li key={c.key}>{c.hint}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
